import { type RequestedSupportingItem } from '@/features/jobs/info-gathering/RequestedSupportingItem'
import { DocumentSupportingItemInput } from '@/features/jobs/info-gathering/documents/DocumentSupportingItemInput'
import { PersonalInfoSupportingItemInput } from '@/features/jobs/info-gathering/individual-info/PersonalInfoSupportingItemInput'
import { SpousalInfoSupportingItemInput } from '@/features/jobs/info-gathering/individual-info/SpousalInfoSupportingItemInput'
import { DependentsInfoSupportingItemInput } from '@/features/jobs/info-gathering/individual-info/DependentsInfoSupportingItemInput'
import { BusinessInfoSupportingItemInput } from '@/features/jobs/info-gathering/business-info/BusinessInfoSupportingItemInput'
import { PersonalRefundMethodInfoSupportingItemInput } from '@/features/jobs/info-gathering/tax-refund-method/PersonalRefundMethodInfoSupportingItemInput'
import { BusinessRefundMethodInfoSupportingItemInput } from '@/features/jobs/info-gathering/tax-refund-method/BusinessRefundMethodInfoSupportingItemInput'
import { NotesSupportingItemInput } from '@/features/jobs/info-gathering/notes/NotesSupportingItemInput'
import { type CapturedInformationTab } from '@/features/jobs/details/DetailsViewModel'

/**
 * Where a completed checklist item links to.
 */
export type SupportingItemPressTarget =
  { type: 'capturedInfo'; tab: CapturedInformationTab } | { type: 'documents' }

/**
 * Returns the navigation target for a completed checklist item,
 * or null if the item is not navigable.
 */
export function getSupportingItemPressTarget(
  item: RequestedSupportingItem,
): SupportingItemPressTarget | null {
  if (!item.completed || !item.input) return null

  if (
    item.input instanceof PersonalInfoSupportingItemInput ||
    item.input instanceof SpousalInfoSupportingItemInput ||
    item.input instanceof DependentsInfoSupportingItemInput
  ) {
    return { type: 'capturedInfo', tab: 'PersonalInfo' }
  }
  if (item.input instanceof BusinessInfoSupportingItemInput) {
    return { type: 'capturedInfo', tab: 'BusinessInfo' }
  }
  if (
    item.input instanceof PersonalRefundMethodInfoSupportingItemInput ||
    item.input instanceof BusinessRefundMethodInfoSupportingItemInput
  ) {
    return { type: 'capturedInfo', tab: 'RefundInfo' }
  }
  if (item.input instanceof NotesSupportingItemInput) {
    return { type: 'capturedInfo', tab: 'Notes' }
  }
  if (item.input instanceof DocumentSupportingItemInput) {
    return { type: 'documents' }
  }
  return null
}

/**
 * Formats the "Provided" text based on the input type.
 */
export function formatSupportingItemProvidedText(
  item: RequestedSupportingItem,
): string {
  if (item.adHoc && !item.completed) {
    return 'Requested'
  }

  if (item.input instanceof DocumentSupportingItemInput) {
    return `${item.input.documents.length} provided`
  }

  return item.completed ? 'Provided' : 'Not provided'
}
